import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import Bugsnag from '@bugsnag/js';

// Components
import LoadingArea from '@molecules/LoadingArea';
import UnauthorizedRedirect from '@molecules/UnauthorizedRedirect';

// Functionality
import useAuthorized from '@hooks/useAuthorized';
import useLocalStorage from '@hooks/useLocalStorage';
import useIcscTracking from '@tracking/useIcscTracking';
import useViewer from '@hooks/useViewer';

export const LoggedInContext = React.createContext();

const LoggedInProvider = ({ children, redirectOnUnauthenticated = false }) => {
    const { viewer } = useViewer();
    const { isLoggedIn } = useAuthorized();
    const { trackEvent } = useIcscTracking();
    const [logInTracked, setLogInTracked] = useLocalStorage('logInTracked', false);

    // Bugsnag
    useEffect(() => {
        if (!viewer) {
            return;
        }

        Bugsnag.setUser(viewer.id, viewer.email, `${viewer.firstName} ${viewer.lastName}`);
    }, [viewer]);

    // Login Tracking
    useEffect(() => {
        if (!logInTracked) {
            // Login
            if (typeof window === 'undefined') return;
            if (!window?.icsc_member_logged_in) return;
            if (!viewer) return;
            trackEvent({ login: 'true', viewer });
            setLogInTracked(true);
        } else {
            // Logout - in rare network failure cases
            // reliably set at templates/default_site/_layouts.group/_default_render_web.html
            if (typeof window === 'undefined') return;
            if (window?.icsc_member_logged_in) return;
            if (!viewer) return;
            setLogInTracked(false);
        }
    }, [isLoggedIn, logInTracked, setLogInTracked, trackEvent, viewer]);

    if (isLoggedIn === false && redirectOnUnauthenticated) {
        return <UnauthorizedRedirect />;
    }

    if (isLoggedIn || !redirectOnUnauthenticated) {
        return <LoggedInContext.Provider value={{ isLoggedIn }}>{children}</LoggedInContext.Provider>;
    }

    return <LoadingArea />;
};

LoggedInProvider.propTypes = {
    children: PropTypes.element.isRequired,
    redirectOnUnauthenticated: PropTypes.bool,
};

export default LoggedInProvider;
